/**
 * The "Volume coletado" card (Figma 125:9, 175:5249): credits, posts and comments actually
 * collected, one line each over the trailing 24 h `CollectionSummaryOut.volume` already carries
 * (independent of whatever window the rest of the page is showing).
 */

import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { CartesianGrid, Line, LineChart, XAxis } from 'recharts';

import { EmptyState, QueryStates } from '../../../chrome/state';
import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import type { ApiFailureError } from '../../../lib/http/errors';
import { Card, CardContent, CardHeader } from '../../../ui/card';
import { Chip } from '../../../ui/chip';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../ui/chart';
import type { CollectionSummary } from './api/schemas';
import { cm } from './messages';

// Slots picked for the frame's green, teal and amber series.
const CONFIG = {
  credits_used: { label: cm('volume.series.credits'), color: 'var(--chart-3)' },
  posts: { label: cm('jobType.posts'), color: 'var(--chart-2)' },
  comments: { label: cm('jobType.comments'), color: 'var(--chart-4)' },
} satisfies ChartConfig;

const TICK_INDEXES = [0, 6, 12, 18];

function isEmptyVolume(data: CollectionSummary): boolean {
  return data.volume.every(
    (bucket) => bucket.credits_used === 0 && bucket.posts === 0 && bucket.comments === 0,
  );
}

function Chart(props: { data: CollectionSummary }): ReactNode {
  const locale = getLocale();
  const points = props.data.volume;
  const lastIndex = points.length - 1;
  const labelOf = (index: number): string =>
    index === lastIndex
      ? cm('volume.now')
      : `${String(new Date(points[index]!.hour).getHours()).padStart(2, '0')}h`;
  const labelByHour = new Map(points.map((point, index) => [point.hour, labelOf(index)]));
  const ticks = [...TICK_INDEXES.filter((i) => i < lastIndex), lastIndex]
    .filter((i) => i >= 0 && i < points.length)
    .map((i) => points[i]!.hour);

  return (
    <ChartContainer
      config={CONFIG}
      role="img"
      aria-label={cm('volume.title')}
      className="aspect-auto h-40 w-full rounded-md focus-within:ring-2 focus-within:ring-ring"
    >
      <LineChart data={points} margin={{ left: 0, right: 0 }}>
        <ChartLegend
          verticalAlign="top"
          align="left"
          content={
            <ChartLegendContent className="justify-start text-caption text-muted-foreground" />
          }
        />
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="hour"
          ticks={ticks}
          tickLine={false}
          axisLine={false}
          tickFormatter={(hour: string) => labelByHour.get(hour) ?? ''}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(_label, payload) => {
                const hour: unknown = payload[0]?.payload?.hour;
                return typeof hour === 'string' ? (labelByHour.get(hour) ?? null) : null;
              }}
              formatter={(value) => (typeof value === 'number' ? integer(value, locale) : '')}
            />
          }
        />
        <Line
          dataKey="credits_used"
          stroke="var(--color-credits_used)"
          strokeWidth={2}
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
        />
        <Line
          dataKey="posts"
          stroke="var(--color-posts)"
          strokeWidth={2}
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
        />
        <Line
          dataKey="comments"
          stroke="var(--color-comments)"
          strokeWidth={2}
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ChartContainer>
  );
}

export function VolumeCard(props: {
  query: UseQueryResult<CollectionSummary, ApiFailureError>;
}): ReactNode {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <h2 className="text-title-sm">{cm('volume.title')}</h2>
        <Chip>{cm('volume.window')}</Chip>
      </CardHeader>
      <CardContent>
        <QueryStates
          query={props.query}
          isEmpty={isEmptyVolume}
          empty={<EmptyState title={cm('volume.empty.title')} tone="neutral" />}
        >
          {(data) => <Chart data={data} />}
        </QueryStates>
      </CardContent>
    </Card>
  );
}
